import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BicycleIcon,
  CloudSunIcon,
  PathIcon,
  SpinnerGapIcon,
  SparkleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { api } from "@/lib/apiClient";
import { buildComparePath } from "@/lib/compareSpots";
import { PlanThisRideButton } from "@/components/PlanThisRideButton";

const driveLabel = (minutes) => {
  if (minutes == null) return translateLabel("Drive unavailable");
  const rounded = Math.max(0, Math.round(minutes));
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  return hours ? `${hours}h${mins ? ` ${mins}m` : ""}` : `${mins} min`;
};

const weatherLabel = (weather) => {
  if (!weather) return translateLabel("Weather unavailable");
  const condition = String(weather.condition || "UNKNOWN")
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/^./, (char) => char.toUpperCase());
  const temperatures = [weather.temp_min_c, weather.temp_max_c]
    .filter((value) => value != null)
    .map((value) => `${Math.round(value)}°`)
    .join("–");
  return [translateLabel(condition), temperatures].filter(Boolean).join(" · ");
};

const currentStatusLabel = (status) => translateLabel(({
  OPEN_NOW: "Open now",
  CLOSED_NOW: "Closed now",
  RESTRICTED: "Restricted",
  UNKNOWN: "Status unknown",
}[status] || "Status unknown"));

export function ParkDecisionSnapshot({ destination }) {
  const destinationId = destination.id;
  const location = useLocation();
  const [context, setContext] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState(null);
  const [answerBusy, setAnswerBusy] = useState("");
  const [answerError, setAnswerError] = useState("");

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      let origin = null;
      try {
        const profile = await api.get("/rider-profile");
        const home = profile.data?.home_location;
        if (home && Number.isFinite(home.lat) && Number.isFinite(home.lng)) {
          origin = home;
          if (active) setOrigin(home);
        }
      } catch {
        // Decision context works without a signed-in/profile origin.
      }

      try {
        const response = await api.get(`/parks/${destinationId}/decision-context`, {
          params: origin ? { lat: origin.lat, lng: origin.lng } : {},
        });
        if (active) setContext(response.data);
      } catch {
        if (active) setContext(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [destinationId]);

  const ask = async (question) => {
    setAnswerBusy(question);
    setAnswerError("");
    try {
      const response = await api.post("/park-decision-answer", {
        destinationId,
        question,
        date: context?.date || undefined,
        ...(origin ? { lat: origin.lat, lng: origin.lng } : {}),
      });
      setAnswer(response.data);
    } catch (error) {
      setAnswerError(error.response?.data?.detail || translateLabel("Park decision assistant is temporarily unavailable."));
    } finally {
      setAnswerBusy("");
    }
  };

  if (loading) {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/[0.07] bg-black/15 px-3 py-3 text-xs text-zinc-500">
        <SpinnerGapIcon size={15} className="animate-spin" /> {translateLabel("Building decision context…")}
      </div>
    );
  }

  if (!context) return null;

  const trailStatus = context.trail_status;
  const hasTrailWarning = (trailStatus.closed + trailStatus.restricted + trailStatus.unknown) > 0;

  const items = [
    {
      icon: PathIcon,
      label: "Park",
      value: currentStatusLabel(context.park_status?.current_status),
      warning: context.park_status?.current_status !== "OPEN_NOW",
    },
    {
      icon: BicycleIcon,
      label: "Trails",
      value: trailStatus.total
        ? translateLabel("{open} open · {closed} closed · {unknown} unknown", { open: trailStatus.open, closed: trailStatus.closed, unknown: trailStatus.unknown })
        : translateLabel("No trail status data"),
      warning: hasTrailWarning,
    },
    {
      icon: CloudSunIcon,
      label: "Weather",
      value: weatherLabel(context.weather),
      warning: !context.weather,
    },
    {
      icon: BicycleIcon,
      label: "Travel",
      value: driveLabel(context.travel?.drive_minutes),
      warning: false,
    },
  ];

  return (
    <div className="mt-4 rounded-2xl border border-white/[0.08] bg-black/15 p-3.5" data-testid="park-decision-snapshot">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.16em] text-[#CCFF00]">{translateLabel("Decision context")}</div>
          <div className="mt-0.5 text-[11px] leading-4 text-zinc-500">{translateLabel("Trusted facts used for park decision support.")}</div>
        </div>
        {context.park_status?.verification?.freshness && (
          <span className="shrink-0 rounded-full border border-white/10 px-2 py-1 text-[9px] font-bold uppercase text-zinc-400">
            {context.park_status.verification.freshness}
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {items.map(({ icon: Icon, label, value, warning }) => (
          <div key={translateLabel(label)} className="min-w-0 rounded-xl border border-white/[0.06] bg-white/[0.025] p-2.5">
            <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.13em] text-zinc-600">
              <Icon size={12} className={warning ? "text-amber-300" : "text-[#CCFF00]"} />
              {translateLabel(label)}
            </div>
            <div className={`mt-1 text-[11px] font-bold leading-4 ${warning ? "text-amber-200" : "text-zinc-200"}`}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {context.warnings?.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-300/15 bg-amber-300/[0.05] px-3 py-2.5 text-[10px] leading-4 text-amber-100">
          <WarningCircleIcon size={14} className="mt-0.5 shrink-0" />
          <span>{context.warnings[0]}{context.warnings.length > 1 ? ` +${context.warnings.length - 1} more uncertainty signal${context.warnings.length > 2 ? "s" : ""}.` : ""}</span>
        </div>
      )}

      <div className="mt-4 border-t border-white/[0.07] pt-3.5" data-testid="park-decision-assistant">
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-[#CCFF00]">
          <SparkleIcon size={13} weight="fill" /> {translateLabel("Ask about this park")}
        </div>
        <p className="mt-1 text-[10px] leading-4 text-zinc-600">
          {translateLabel("Guided answers use only the trusted context above.")}
        </p>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {[
            ["WORTH_GOING", translateLabel("Worth going?")],
            ["RIDING_FIT", translateLabel("Good fit for me?")],
            ["BEFORE_YOU_GO", translateLabel("Before I go")],
          ].map(([question, label]) => (
            <button
              key={question}
              type="button"
              onClick={() => ask(question)}
              disabled={Boolean(answerBusy)}
              className="rounded-full border border-white/10 bg-white/[0.035] px-3 py-2 text-[10px] font-bold text-zinc-300 transition-colors hover:border-[#CCFF00]/25 hover:text-[#CCFF00] disabled:opacity-40"
              data-testid={`park-ai-${question.toLowerCase()}`}
            >
              {answerBusy === question ? translateLabel("Thinking…") : label}
            </button>
          ))}
        </div>

        {answerError && (
          <div className="mt-3 rounded-xl border border-red-400/15 bg-red-400/[0.05] px-3 py-2.5 text-[10px] leading-4 text-red-200">
            {answerError}
          </div>
        )}

        {answer && (
          <div className="mt-3 rounded-xl border border-[#CCFF00]/15 bg-[#CCFF00]/[0.04] p-3" data-testid="park-ai-answer">
            <p className="text-xs font-bold leading-5 text-white">{answer.summary}</p>
            {answer.reasons?.length > 0 && (
              <ul className="mt-2 space-y-1 text-[10px] leading-4 text-zinc-300">
                {answer.reasons.map((reason) => <li key={reason}>• {reason}</li>)}
              </ul>
            )}
            {answer.cautions?.length > 0 && (
              <div className="mt-2 space-y-1 border-t border-amber-300/10 pt-2 text-[10px] leading-4 text-amber-100">
                {answer.cautions.map((caution) => (
                  <div key={caution} className="flex items-start gap-1.5">
                    <WarningCircleIcon size={11} className="mt-0.5 shrink-0" />
                    <span>{caution}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {answer && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/[0.07] pt-3" data-testid="park-ai-handoffs">
            <Link
              to={buildComparePath([destination], {
                searchCenter: origin ? {
                  lat: origin.lat,
                  lng: origin.lng,
                  name: origin.name || origin.label || translateLabel("Starting point"),
                } : undefined,
                date: context?.date || undefined,
                returnTo: `${location.pathname}${location.search || ""}`,
              })}
              className="inline-flex items-center rounded-lg border border-white/10 bg-white/[0.035] px-2.5 py-1.5 text-[11px] font-bold text-zinc-300 hover:border-white/20 hover:text-white"
              data-testid="park-ai-compare"
            >
              {translateLabel("Compare this park")}
            </Link>
            <PlanThisRideButton
              destination={destination}
              initialDate={context?.date || ""}
              initialOrigin={origin}
              source="park_ai"
              compact
            />
          </div>
        )}
      </div>
    </div>
  );
}
